import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  fetchOrganizationSessionsReport,
  downloadOrganizationSessionsReport,
  type OrgSessionReportRow,
  type OrganizationUnitSummary,
} from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Download, AlertTriangle } from "@/components/icons";
import { PaymentCell } from "@/components/PaymentStatus";

// Number of table columns (kept in one place so the loading/empty spanning
// rows stay in sync when a column is added).
const COL_COUNT = 11;

function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return `$${v.toFixed(2)}`;
}

function kwh(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return v.toFixed(3);
}

// The backend hands sessions over as "YYYY-MM-DD HH:MM:SS" (UTC). The table
// shows the day US-style to match the date fields above it; the clock is left
// alone, and unrecognized shapes are printed verbatim rather than guessed at.
function reportTimestamp(raw: string | null | undefined): string {
  if (!raw) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](.+?)Z?$/.exec(raw.trim());
  if (!m) return raw;
  return `${m[2]}/${m[3]}/${m[1]} ${m[4]}`;
}

// A unit id like "0910042001280660123222723534" is far too long for the table
// column. When the unit has a custom name we show that; otherwise we show the
// last few digits with a leading ellipsis (full id stays on hover / in the CSV).
const UNIT_ID_TAIL = 6;

function shortUnitId(unitId: string): string {
  if (unitId.length <= UNIT_ID_TAIL) return unitId;
  return `…${unitId.slice(-UNIT_ID_TAIL)}`;
}

// Same "has a real custom name" rule as the dashboard unit table: a name that
// still equals the auto-generated "Unit <id>" placeholder doesn't count.
function customUnitName(u: OrganizationUnitSummary): string | null {
  return u.name && u.name !== `Unit ${u.unit_id}` ? u.name : null;
}

/**
 * In-UI charging-sessions report for an organization (admin only).
 *
 * Same data as the CSV export, rendered as a table with a totals summary row,
 * plus a button to download the identical CSV for the selected date range.
 * A Payment column surfaces each session's payment status and, for money that
 * never captured, the diagnostics to chase it.
 */
export function OrgSessionsReport({
  orgId,
  units,
}: {
  orgId: number;
  /** Org units (with custom names) so the table can label a row by name. */
  units?: OrganizationUnitSummary[];
}) {
  // unit_id -> custom name, for labelling report rows.
  const unitNames = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of units ?? []) {
      const name = customUnitName(u);
      if (name) m.set(u.unit_id, name);
    }
    return m;
  }, [units]);
  // Input values vs. the range actually applied to the query (set on "Run").
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [applied, setApplied] = useState<{ start?: string; end?: string }>({});
  // False while a hand-typed date field holds text that isn't a date.
  const [datesTypable, setDatesTypable] = useState(true);
  const [hasRun, setHasRun] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Client-side filter: show only sessions whose payment needs chasing.
  const [attentionOnly, setAttentionOnly] = useState(false);

  // Don't fetch on mount — only after the user clicks "Run report". If a report
  // for the same range was already fetched earlier this session, react-query
  // serves it from cache (so it may appear immediately), but we never kick off
  // a request just because the page opened.
  const reportQ = useQuery({
    queryKey: ["org", orgId, "sessions-report", applied.start ?? "", applied.end ?? ""],
    queryFn: () => fetchOrganizationSessionsReport(orgId, applied),
    enabled: hasRun,
  });

  // Guard shared by "Run report" and "Export CSV" — a report must never quietly
  // fall back to the default period because a typed date didn't parse.
  const rangeRejected = (range: { start?: string; end?: string }) => {
    if (!datesTypable) {
      toast.error('Enter dates as MM/DD/YYYY, or a phrase like "last month"');
      return true;
    }
    if (range.start && range.end && range.end < range.start) {
      toast.error("End date must be on or after start date");
      return true;
    }
    return false;
  };

  // Every click must produce a run. Setting `applied` to the range it already
  // holds leaves the query key untouched, so react-query serves the cached
  // report and the button looks dead — which is exactly what it looks like
  // after re-typing the same dates, or after a phrase resolves to the range
  // that is already loaded. Same range = explicit refetch.
  // `range` is passed explicitly by the Enter key: the picker resolves the
  // keystroke ("last month" → two days) and hands the result over, because this
  // component's own start/end state hasn't re-rendered yet at that point.
  const runReport = (range?: { start?: string; end?: string }) => {
    const next = range ?? { start: start || undefined, end: end || undefined };
    if (rangeRejected(next)) return;
    const unchanged = hasRun && next.start === applied.start && next.end === applied.end;
    setApplied(next);
    setHasRun(true);
    if (unchanged) reportQ.refetch();
  };

  const handleDownload = async () => {
    if (rangeRejected({ start: start || undefined, end: end || undefined })) return;
    setDownloading(true);
    try {
      // Export honors the current date fields, independent of "Run report".
      const { blob, filename } = await downloadOrganizationSessionsReport(orgId, {
        start: start || undefined,
        end: end || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e: any) {
      toast.error(e?.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const report = reportQ.data;
  const allRows: OrgSessionReportRow[] = report?.rows ?? [];
  const totals = report?.totals;
  const attentionCount = totals?.payment_attention_count ?? 0;
  const rows = useMemo(
    () => (attentionOnly ? allRows.filter((r) => r.payment_needs_attention) : allRows),
    [allRows, attentionOnly],
  );

  return (
    <TooltipProvider delayDuration={100}>
      <Card>
        <CardHeader>
          <CardTitle>Charging Sessions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2">
            <DateRangePicker
              start={start || undefined}
              end={end || undefined}
              onChange={(s, e) => {
                setStart(s ?? "");
                setEnd(e ?? "");
              }}
              onValidityChange={setDatesTypable}
              onSubmit={(s, e) => runReport({ start: s, end: e })}
            />
            <Button size="sm" onClick={() => runReport()} disabled={reportQ.isFetching}>
              {reportQ.isFetching ? "Loading..." : "Run report"}
            </Button>
            <Button size="sm" variant="outline" onClick={handleDownload} disabled={downloading || reportQ.isError}>
              <Download className="h-3.5 w-3.5 mr-1.5" />
              {downloading ? "Generating..." : "Export CSV"}
            </Button>
            <span className="text-xs opacity-60">Empty range = last 31 days (max 366 days). Times are UTC.</span>
          </div>

          {/* Payment-attention banner. CONFIRMED outstanding (finalized amount due
              minus captured) is stated as a dollar figure; sessions whose final
              charge is unknown are stated as a COUNT — never dollarised, so an
              authorization hold can never masquerade as money owed. */}
          {report && attentionCount > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                <span className="font-semibold">{attentionCount}</span> session
                {attentionCount === 1 ? "" : "s"} need payment attention —{" "}
                <span className="font-semibold">
                  {money(totals?.total_known_outstanding_amount)}
                </span>{" "}
                confirmed outstanding
                {totals && totals.unknown_outstanding_session_count > 0 && (
                  <>
                    ,{" "}
                    <span className="font-semibold">
                      {totals.unknown_outstanding_session_count}
                    </span>{" "}
                    more with an unknown final amount
                  </>
                )}
                .
                {totals && (
                  <span className="opacity-80">
                    {" "}
                    ({totals.payment_action_required_count} payment action,{" "}
                    {totals.engineering_investigation_count} engineering,{" "}
                    {totals.manual_review_required_count} manual review)
                  </span>
                )}
              </span>
              <Button
                size="xs"
                variant={attentionOnly ? "default" : "outline"}
                className="ml-auto"
                onClick={() => setAttentionOnly((v) => !v)}
              >
                {attentionOnly ? "Show all" : "Show only these"}
              </Button>
            </div>
          )}

          {/* Table */}
          {reportQ.isError ? (
            <div className="text-sm text-error">Failed to load report</div>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Session Start (UTC)</th>
                    <th className="px-3 py-2 font-medium">Unit</th>
                    <th className="px-3 py-2 font-medium">Driver</th>
                    <th className="px-3 py-2 font-medium text-right">Energy (kWh)</th>
                    <th className="px-3 py-2 font-medium text-right">Billed</th>
                    <th className="px-3 py-2 font-medium text-right">Captured</th>
                    <th className="px-3 py-2 font-medium">Payment</th>
                    <th className="px-3 py-2 font-medium text-right">Est. Stripe Fee</th>
                    <th className="px-3 py-2 font-medium text-right">VoltiE Fee</th>
                    <th className="px-3 py-2 font-medium text-right">Payout to Org</th>
                    <th className="px-3 py-2 font-medium text-right">Org Owes VoltiE</th>
                  </tr>
                </thead>
                <tbody>
                  {reportQ.isFetching ? (
                    <tr>
                      <td colSpan={COL_COUNT} className="px-3 py-6 text-center opacity-60">Loading...</td>
                    </tr>
                  ) : !report ? (
                    <tr>
                      <td colSpan={COL_COUNT} className="px-3 py-6 text-center opacity-60">
                        Choose a date range (optional) and click “Run report”.
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={COL_COUNT} className="px-3 py-6 text-center opacity-60">
                        {attentionOnly
                          ? "No sessions need payment attention in this range"
                          : "No charging sessions in this range"}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, i) => (
                      <tr key={i} className="border-t border-border hover:bg-muted/30">
                        <td className="px-3 py-1.5 font-mono whitespace-nowrap">
                          {reportTimestamp(r.start_time)}
                        </td>
                        {(() => {
                          const name = unitNames.get(r.unit_id);
                          return (
                            <td
                              className={`px-3 py-1.5 whitespace-nowrap ${name ? "" : "font-mono"}`}
                              title={r.unit_id}
                            >
                              {name ?? shortUnitId(r.unit_id)}
                            </td>
                          );
                        })()}
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          {r.customer_name || <span className="opacity-50">—</span>}
                        </td>
                        <td className="px-3 py-1.5 text-right">{kwh(r.energy_kwh)}</td>
                        <td className="px-3 py-1.5 text-right">{money(r.billed_amount)}</td>
                        <td className="px-3 py-1.5 text-right">{money(r.captured_payment)}</td>
                        <td className="px-3 py-1.5"><PaymentCell r={r} /></td>
                        <td className="px-3 py-1.5 text-right">{money(r.estimated_stripe_fee)}</td>
                        <td className="px-3 py-1.5 text-right">{money(r.voltie_fee)}</td>
                        <td className="px-3 py-1.5 text-right">{money(r.payout_to_organization)}</td>
                        <td className="px-3 py-1.5 text-right">{money(r.organization_owes_voltie)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
                {totals && rows.length > 0 && (
                  <tfoot className="border-t-2 border-border bg-muted/40 font-semibold">
                    <tr>
                      <td className="px-3 py-2">Totals</td>
                      <td className="px-3 py-2">{totals.total_sessions} sessions</td>
                      <td className="px-3 py-2"></td>
                      <td className="px-3 py-2 text-right">{kwh(totals.total_energy_kwh)}</td>
                      <td className="px-3 py-2 text-right">{money(totals.total_billed_amount)}</td>
                      <td className="px-3 py-2 text-right">{money(totals.total_captured_payment)}</td>
                      <td className="px-3 py-2">
                        {totals.known_outstanding_session_count > 0 ||
                        totals.unknown_outstanding_session_count > 0 ? (
                          <span className="text-amber-600 dark:text-amber-400">
                            {money(totals.total_known_outstanding_amount)} outstanding
                            {totals.unknown_outstanding_session_count > 0 &&
                              ` +${totals.unknown_outstanding_session_count} unknown`}
                          </span>
                        ) : (
                          <span className="opacity-50">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">{money(totals.total_estimated_stripe_fee)}</td>
                      <td className="px-3 py-2 text-right">{money(totals.total_voltie_fee)}</td>
                      <td className="px-3 py-2 text-right">{money(totals.total_payout_to_organization)}</td>
                      <td className="px-3 py-2 text-right">{money(totals.total_organization_owes_voltie)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
